/* eslint-disable @typescript-eslint/no-explicit-any */

import type { CSSProperties } from "react";

export interface Theme {
  layout?: string,
  colors?: {
    background?: string;
    base?: string;
    primary?: string;
    primary_contrast?: string;
    secondary?: string;
    secondary_contrast?: string;
    neutral?: string;
    neutral_contrast?: string;
    error?: string;
    error_contrast?: string;
    success?: string;
    success_contrast?: string;
    border?: string;
    border_choice?: string;
    border_choice_input?: string;
  },
  font_family?: string;
  font_family_url?: string;
  components?: {
    border_radius?: string;
    border_radius_offer_box?: string;
    border_radius_choice_box?: string;
    offer_box_padding?: string;
    choice_box_padding?: string;
  }
}

export enum State {
  'AL' = 'AL',
  'AK' = 'AK',
  'AS' = 'AS',
  'AZ' = 'AZ',
  'AR' = 'AR',
  'CA' = 'CA',
  'CO' = 'CO',
  'CT' = 'CT',
  'DE' = 'DE',
  'DC' = 'DC',
  'FM' = 'FM',
  'FL' = 'FL',
  'GA' = 'GA',
  'GU' = 'GU',
  'HI' = 'HI',
  'ID' = 'ID',
  'IL' = 'IL',
  'IN' = 'IN',
  'IA' = 'IA',
  'KS' = 'KS',
  'KY' = 'KY',
  'LA' = 'LA',
  'ME' = 'ME',
  'MH' = 'MH',
  'MD' = 'MD',
  'MA' = 'MA',
  'MI' = 'MI',
  'MN' = 'MN',
  'MS' = 'MS',
  'MO' = 'MO',
  'MT' = 'MT',
  'NE' = 'NE',
  'NV' = 'NV',
  'NH' = 'NH',
  'NJ' = 'NJ',
  'NM' = 'NM',
  'NY' = 'NY',
  'NC' = 'NC',
  'ND' = 'ND',
  'MP' = 'MP',
  'OH' = 'OH',
  'OK' = 'OK',
  'OR' = 'OR',
  'PW' = 'PW',
  'PA' = 'PA',
  'PR' = 'PR',
  'RI' = 'RI',
  'SC' = 'SC',
  'SD' = 'SD',
  'TN' = 'TN',
  'TX' = 'TX',
  'UT' = 'UT',
  'VT' = 'VT',
  'VI' = 'VI',
  'VA' = 'VA',
  'WA' = 'WA',
  'WV' = 'WV',
  'WI' = 'WI',
  'WY' = 'WY',
}

export enum Province {
  'AB' = 'AB',
  'BC' = 'BC',
  'MB' = 'MB',
  'NB' = 'NB',
  'NL' = 'NL',
  'NS' = 'NS',
  'ON' = 'ON',
  'PE' = 'PE',
  'QC' = 'QC',
  'SK' = 'SK',
  'NT' = 'NT',
  'NU' = 'NU',
  'YT' = 'YT'
}

export type StateType = keyof typeof State;
export type ProvinceType = keyof typeof Province;

export enum EventType {
  'init' = 'vi.element.init',
  'update' = 'vi.element.update',
  'resize' = 'vi.element.resize',
  'validate' = 'vi.element.validate',
  'ready' = 'vi.element.ready',
  'offer_state_ready' = 'vi.element.offer_state_ready',
  'offer_state_change' = 'vi.element.offer_state_change',
  'tokenize_payment' = 'vi.element.tokenize_payment',
  'event_insurance_requirements_state_change' = 'vi.element.event_insurance_requirements_state_change',
  'event_exhibitor_liability_state_change' = 'vi.element.event_exhibitor_liability_state_change'
}

export interface Customer {
  first_name: string;
  last_name: string;
  email_address: string;
  street?: string;
  city?: string;
  state: StateType | ProvinceType;
  postal_code: string;
  phone_number?: string;
  country?: "US" | "CA"
}

export interface ProductDetails {
  name: string;
  friendly_name: string;
  promotional_header: string;
  promotional_description: string;
  descriptors?: string[];
  legal_disclaimer: string;
  perils?: string[];
  carrier_logo_url?: string;
  carrier_name?: string;
}

export interface QuoteRequest {
  quote_id?: string;
  offer_id?: string;
  customer?: Customer;
  // biome-ignore lint/suspicious/noExplicitAny: dynamic values
  policy_attributes?: Record<string, any>;
  // biome-ignore lint/suspicious/noExplicitAny: dynamic values
  attributes?: Record<string, any>;
  /** @internal */
  default_selected?: boolean;
}

export interface Decision {
  options: {
    status: string;
    error?: {
      code: string;
      message: string;
    },
    data?: QuoteResponse
  }[];
}

export interface ApiResponseV2 {
  offer_id?: string;
  decisions: Decision[];
}

export interface QuoteResponse {
  policy_number?: string;
  policy_status: string;
  quote_id: string;
  offer_id: string;
  vid_id: string;
  policy_holder: Customer;
  // biome-ignore lint/suspicious/noExplicitAny: dynamic values
  policy_attributes: Record<string, any>;
  total: number;
  currency: string;
  product: ProductDetails;
  // biome-ignore lint/suspicious/noExplicitAny: dynamic values
  metadata?: Record<string, any>;
  partner?: {
    id?: string;
    legal_business_name?: string;
  };
  subscription_premium?: {
    payment_amount?: number;
    payments_required?: number;
    can_cancel?: boolean;
  };
}

export interface ErrorResponse {
  error: string;
  error_code: string;
}

export interface PolicyAttributeSchema {
  type: string;
  properties: Record<string, {
    type: string;
    properties?: Record<string, {
      type: string;
      description: string;
    }>;
  }>;
}

export interface ProductResponse {
  product: ProductDetails;
  policy_attributes_schema: PolicyAttributeSchema;
}

export interface ProductConfig {
  [key: string]: QuoteRequest[];
}

export interface PaymentElementConfig {
  enabled?: boolean;
  button?: boolean;
  customer_secret?: string;
}

export interface Config {
  /**
   * Client ID part of your API key.
   */
  client_id: string;

  /**
   * Partner client secret
   * Required for EventInsuranceRequirement and EventExhibitorLiability components.
   */
  partner_client_secret?: string;

  /**
   * Theme options
   */
  theme?: Theme;

  /**
   * Offer ID obtained from VerticalInsure.trackOffer();
   */
  tracking_offer_id?: string;

  /**
   * A unique id for this offer. This value should be defined if there are 
   * multiple offers being presented on a single page. You can typically
   * set this value to an order id, or an item id. 
   */
  unique_offer_id?: string;

  /**
   * Payment configuration
   */
  payments?: PaymentElementConfig;

  /**
   * Customer details. If this customer is provided, it will override any customer
   * defined in any specific product configuration.
   */
  customer?: Customer;

  /**
   * Configuration / quote information for various products
   */
  product_config?: ProductConfig;

  /**
   * Hide offer on specific errors: state_not_available, activity_not_available. Default is true.
   */
  hide_on_error?: boolean;

  /**
   * Component type to determine which route to navigate to.
   * Default is 'offer'
   */
  component_type?: string;

  /**
   * Additional configuration for the specified component
   */
  component_config?: ComponentConfig;
}

export interface BaseMessage {
  eventType: EventType
}

export interface ResizeMessage extends BaseMessage {
  eventType: EventType.resize,
  height: number;
  width: number;
}

export interface ReadyMessage extends BaseMessage {
  eventType: EventType.ready
}

export interface InitMessage extends Config, BaseMessage {
  eventType: EventType.init;
  version: string;
  origin: string;
}

export interface ValidateMessage extends BaseMessage {
  eventType: EventType.validate;
  is_valid?: boolean;
}

export interface TokenizePaymentMessage extends BaseMessage {
  eventType: EventType.tokenize_payment;
  is_valid?: boolean;
  error?: {
    code?: string;
    message?: string;
  },
  token?: string;
}

export interface OfferStateChangeMessage extends BaseMessage {
  eventType: EventType.offer_state_change;
  quotes: QuoteResponse[];
}

export interface OfferReadyMessage extends BaseMessage {
  eventType: EventType.offer_state_ready;
  offersAvailable: boolean;
}

export interface EventInsuranceRequirementsStateChangeMessage extends BaseMessage {
  eventType: EventType.event_insurance_requirements_state_change;
  event: {
    event_id: string;
    event_insurance_required?: boolean;
  };
}

export enum EventExhibitorLiabilityStatus {
  'COIValid' = 'COIValid',
  'COIManualReview' = 'COIManualReview',
  'OfferAccept' = 'OfferAccept',
  'OfferDecline' = 'OfferDecline',
  'OfferPurchased' = 'OfferPurchased',
  'CompleteLater' = 'CompleteLater'
}

export interface EventExhibitorLiabilityStateChangeMessage extends BaseMessage {
  eventType: EventType.event_exhibitor_liability_state_change;
  event_exhibitor: {
    id: string;
    status?: EventExhibitorLiabilityStatus;
    quote?: QuoteResponse;
    coi_validation_failures?: {code: string, text: string}[];
    manual_review_request?: string;
  };
}

export interface EventInsuranceRequirementsStateChangeEvent {
  event_id: string;
  event_insurance_required?: boolean;
}

export interface EventExhibitorLiabilityStateChangeEvent {
  id: string;
  status?: EventExhibitorLiabilityStatus | null;
  quote?: QuoteResponse;
  coi_validation_failures?: {code: string, text: string}[] | null;
}

export interface OfferStateChangeEvent {
  unique_offer_id?: string;
  quotes: QuoteResponse[];
}

export interface OfferReadyEvent {
  offersAvailable: boolean;
}

export interface UpdateMessage extends BaseMessage {
  eventType: EventType.update,
  product_config: ProductConfig;
}

export interface OfferConfig extends Omit<InitMessage, 'eventType'> {
  styles: CSSProperties;
  font_url?: string;
  is_test: boolean;
}

export interface QuoteState {
  [key: string]: (QuoteResponse | ErrorResponse | ApiResponseV2)[];
}

export type OfferContextType = {
  loading: boolean;
  quotes: QuoteState;
  config?: OfferConfig;
  updateProductConfig: (productConfig: ProductConfig) => void;
  shouldDisplayLayout?: boolean;
  hasValidQuote?: boolean;
  acceptedQuotes: string[];
}

export interface CustomerClientSecret {
  customer_id: string;
  customer_email: string;
  secret: string;
}

export type ProductQuotes = (QuoteResponse | ErrorResponse | ApiResponseV2)[];

export interface EventExhibitor {
  id?: string;
  name: string;
  street: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
  accepted: boolean;
}

export interface EventConfig {
  id?: string;
  name: string;
  start_date: string;
  end_date: string;
  street: string;
  city: string;
  state: string;
  postal_code: string;
  country: string;
}

export interface ComponentConfig {

  /**
   * Event configuration for insurance requirements component
   */
  event_config?: EventConfig;

  /**
   * Event exhibitor configuration for exhibitor liability component
   */
  event_exhibitor_config?: EventExhibitor;
}